// legacy-fonts/krutiDev055.js

import {
  convertKrutiDev010ToUnicode,
} from './krutiDev010';

/**
 * Kruti Dev 055 is commonly found in older Marathi documents.
 *
 * Its encoding is very close to Kruti Dev 010, but important differences
 * exist for Marathi text, particularly:
 *
 *   श <-> ष
 *   अे -> ए
 *   अै -> ऐ
 *   Ú + य -> र्‍य
 *
 * Therefore we first run the standard Kruti Dev conversion and then
 * normalize the known Kruti Dev 055 differences.
 */


/**
 * Swap श and ष safely.
 *
 * A temporary placeholder is necessary. Doing:
 *
 *   text.replace(/श/g, 'ष').replace(/ष/g, 'श')
 *
 * would simply turn everything into श.
 */
const swapShaAndSsa = (text = '') => {
  const PLACEHOLDER = '\uE000';

  return text
    .replace(/श/g, PLACEHOLDER)
    .replace(/ष/g, 'श')
    .replace(new RegExp(PLACEHOLDER, 'g'), 'ष');
};


/**
 * Fix the Marathi र्‍य conjunct found in Kruti Dev 055.
 *
 * Examples seen in converted Marathi documents:
 *
 *   असणाÚ यांना  -> असणार्‍यांना
 *   पांढ Ú या    -> पांढर्‍या
 *   ब Ú याच      -> बर्‍याच
 *
 * Ú is left behind by a normal Kruti Dev 010 conversion because
 * Kruti Dev 055 uses it specially.
 */
const fixMarathiRya = (text = '') =>
  text
    // Ú followed by Unicode य
    .replace(/\s*Ú\s*य/g, 'र्‍य')

    // Defensive case: Ú immediately before a vowel sign or other
    // continuation where the य may have been consumed differently.
    .replace(/\s*Ú\s*/g, 'र्‍य');


/**
 * Fix independent vowels produced incorrectly by a generic
 * Kruti Dev converter.
 *
 * Kruti Dev 055 Marathi samples have been observed to produce:
 *
 *   अेक     -> एक
 *   अेवढी   -> एवढी
 *   अेरिया  -> एरिया
 *
 *   अैकणार  -> ऐकणार
 */
const fixIndependentVowels = (text = '') =>
  text
    .replace(/अे/g, 'ए')
    .replace(/अै/g, 'ऐ');


/**
 * Some legacy Word/PDF extraction produces visually identical but
 * typographically different whitespace.
 */
const normalizeInput = (text = '') =>
  text
    .replace(/\u00A0/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');


/**
 * Converts Kruti Dev 055 encoded Marathi text to Unicode Devanagari.
 *
 * @param {string} text
 * @returns {string}
 */
export const convertKrutiDev055ToUnicode = (text = '') => {
  if (!text) return '';

  const input = normalizeInput(text);

  /*
   * Step 1:
   * Kruti Dev 055 largely follows the normal Kruti Dev encoding,
   * so perform the standard conversion first.
   */
  let converted =
    convertKrutiDev010ToUnicode(input);

  /*
   * Step 2:
   * Correct Kruti Dev 055's Marathi-specific श / ष mapping.
   */
  converted = swapShaAndSsa(converted);

  /*
   * Step 3:
   * Correct malformed independent vowels.
   */
  converted = fixIndependentVowels(converted);

  /*
   * Step 4:
   * Handle the special Marathi र्‍य representation.
   */
  converted = fixMarathiRya(converted);

  /*
   * Step 5:
   * Canonical Unicode normalization.
   */
  return converted.normalize('NFC');
};